import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Server, Calendar, AlertTriangle, Power, Check } from 'lucide-react';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  EmptyState,
  Spinner,
  Stat,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
} from '@pnl/ui';
import type { PlatformBillingStatusKey, PlatformKindKey } from '@pnl/sdk';
import { PnlApiError } from '@pnl/sdk';
import { api } from '../lib/api';

const STATUS_TONE: Record<PlatformBillingStatusKey, 'warning' | 'success' | 'danger' | 'neutral'> =
  {
    PENDING: 'warning',
    PAID: 'success',
    OVERDUE: 'danger',
    SUSPENDED: 'neutral',
  };

const PLATFORM_LABEL: Record<PlatformKindKey, string> = {
  PROFIT_B3: 'Profit · B3',
  BLACKARROW_FOREX: 'Market Data · Forex',
};

export function PlatformsPage() {
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<PlatformBillingStatusKey | 'ALL'>('ALL');
  const [feedback, setFeedback] = useState<string | null>(null);

  const billingsQuery = useQuery({
    queryKey: ['admin', 'platforms', 'billings', { status: statusFilter }],
    queryFn: () =>
      api.listPlatformBillings({
        page: 1,
        pageSize: 100,
        ...(statusFilter !== 'ALL' ? { status: statusFilter } : {}),
      }),
  });

  const markPaid = useMutation({
    mutationFn: (id: string) => api.markPlatformBillingPaid(id, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'platforms', 'billings'] });
      setFeedback('Cobrança marcada como paga.');
      setTimeout(() => setFeedback(null), 3500);
    },
  });

  const suspend = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      api.suspendPlatformBilling(id, reason),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'platforms', 'billings'] });
      setFeedback('Plataforma suspensa pra esse trader.');
      setTimeout(() => setFeedback(null), 3500);
    },
  });

  const reactivate = useMutation({
    mutationFn: (id: string) => api.reactivatePlatformBilling(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'platforms', 'billings'] });
      setFeedback('Plataforma reativada (cobrança volta a pendente).');
      setTimeout(() => setFeedback(null), 3500);
    },
  });

  const items = billingsQuery.data?.items ?? [];

  const counts = useMemo(
    () => ({
      paid: items.filter((b) => b.status === 'PAID').length,
      pending: items.filter((b) => b.status === 'PENDING').length,
      overdue: items.filter((b) => b.status === 'OVERDUE').length,
      suspended: items.filter((b) => b.status === 'SUSPENDED').length,
    }),
    [items],
  );

  const errorMsg = (err: unknown): string =>
    err instanceof PnlApiError ? err.api.message : err instanceof Error ? err.message : 'Erro';

  return (
    <div className="space-y-6">
      <div>
        <p className="text-2xs text-brand-500 font-semibold uppercase tracking-widest">Catálogo</p>
        <h1 className="font-display text-ink-900 mt-1 text-3xl font-semibold tracking-tight">
          Plataformas
        </h1>
        <p className="text-ink-500 mt-1 text-sm">
          Controle manual da mensalidade da Profit (B3) e BlackArrow (Forex funded).
        </p>
      </div>

      <Alert tone="info" title="Modelo manual">
        Admin marca como paga + opcionalmente upload de comprovante. Suspensão sempre manual.
        Migração para cobrança automática via Stripe Subscription separada está no roadmap.
      </Alert>

      {feedback && (
        <Alert tone="success" title="Atualizado">
          {feedback}
        </Alert>
      )}
      {(markPaid.error || suspend.error || reactivate.error) && (
        <Alert tone="danger" title="Falhou">
          {errorMsg(markPaid.error ?? suspend.error ?? reactivate.error)}
        </Alert>
      )}

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardBody>
            <Stat label="Pagas" value={String(counts.paid)} caption="Cobranças quitadas" />
            <Check size={16} className="text-success-500 mt-2" />
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <Stat label="Pendentes" value={String(counts.pending)} caption="Aguardando pagamento" />
            <Calendar size={16} className="text-warning-500 mt-2" />
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <Stat label="Vencidas" value={String(counts.overdue)} caption="Pra ação do admin" />
            <AlertTriangle size={16} className="text-danger-500 mt-2" />
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <Stat label="Suspensas" value={String(counts.suspended)} caption="Acesso bloqueado" />
            <Power size={16} className="text-ink-400 mt-2" />
          </CardBody>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-3">
            <CardTitle>Cobranças</CardTitle>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as PlatformBillingStatusKey | 'ALL')}
              className="border-ink-200 focus:border-brand-500 focus:ring-brand-500/30 rounded-lg bg-white px-3 py-1.5 text-xs focus:outline-none focus:ring-2"
            >
              <option value="ALL">Todos</option>
              <option value="PENDING">Pendentes</option>
              <option value="PAID">Pagas</option>
              <option value="OVERDUE">Vencidas</option>
              <option value="SUSPENDED">Suspensas</option>
            </select>
          </div>
        </CardHeader>
        <CardBody>
          {billingsQuery.isLoading ? (
            <div className="flex justify-center py-10">
              <Spinner />
            </div>
          ) : items.length === 0 ? (
            <EmptyState
              icon={<Server size={28} />}
              title="Sem cobranças"
              description="Conforme as assinaturas viram ACTIVE, a plataforma de cada trader aparece aqui."
            />
          ) : (
            <Table>
              <THead>
                <TR>
                  <TH>Trader</TH>
                  <TH>Plataforma</TH>
                  <TH>Mês ref.</TH>
                  <TH>Vencimento</TH>
                  <TH>Status</TH>
                  <TH className="text-right">Ações</TH>
                </TR>
              </THead>
              <TBody>
                {items.map((b) => (
                  <TR key={b.id}>
                    <TD>
                      <div className="text-ink-900 text-sm font-semibold">{b.traderFullName}</div>
                      <div className="text-ink-500 text-xs">{b.traderEmail}</div>
                    </TD>
                    <TD>
                      <span className="text-ink-700 text-xs">{PLATFORM_LABEL[b.platform]}</span>
                    </TD>
                    <TD>
                      <span className="font-mono text-xs">{b.referenceMonth.slice(0, 7)}</span>
                    </TD>
                    <TD>
                      <span className="font-mono text-xs">{b.dueDate}</span>
                    </TD>
                    <TD>
                      <Badge tone={STATUS_TONE[b.status]}>{b.status}</Badge>
                    </TD>
                    <TD className="text-right">
                      <div className="flex justify-end gap-2">
                        {b.status !== 'PAID' && b.status !== 'SUSPENDED' && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => markPaid.mutate(b.id)}
                            disabled={markPaid.isPending}
                          >
                            <Check size={12} /> Marcar paga
                          </Button>
                        )}
                        {b.status === 'OVERDUE' && (
                          <Button
                            size="sm"
                            variant="danger"
                            onClick={() => {
                              if (window.confirm('Suspender a plataforma deste trader?')) {
                                suspend.mutate({ id: b.id });
                              }
                            }}
                            disabled={suspend.isPending}
                          >
                            <Power size={12} /> Suspender
                          </Button>
                        )}
                        {b.status === 'SUSPENDED' && (
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => {
                              if (window.confirm('Reativar a plataforma deste trader?')) {
                                reactivate.mutate(b.id);
                              }
                            }}
                            disabled={reactivate.isPending}
                          >
                            <Power size={12} /> Reativar
                          </Button>
                        )}
                      </div>
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Regras vigentes</CardTitle>
        </CardHeader>
        <CardBody className="space-y-3 text-sm">
          <div>
            <p className="text-ink-900 font-semibold">B3 (Profit)</p>
            <p className="text-ink-700 mt-1">
              30 dias grátis após ativação + 3 dias de tolerância. Após o dia 33, status vira{' '}
              <Badge tone="warning">OVERDUE</Badge>. Admin decide se suspende manualmente.
            </p>
          </div>
          <div>
            <p className="text-ink-900 font-semibold">Forex teste (BlackArrow)</p>
            <p className="text-ink-700 mt-1">
              Plataforma inclusa durante a fase de avaliação. Sem cobrança separada.
            </p>
          </div>
          <div>
            <p className="text-ink-900 font-semibold">Forex funded (BlackArrow)</p>
            <p className="text-ink-700 mt-1">
              Após aprovação, taxa mensal cobrada separadamente. Lançamento manual pelo admin até
              cobrança automática via Stripe Subscription separada.
            </p>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}
